import { useMemo, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminKYCApi } from '@/api/admin/kyc';
import { scrollToTop } from '@/routing';
import {
  ENABLED_QUESTS_TO_INTERACT_STATUSES_ADMIN,
  SHOWED_SECTIONS_COUNT,
  STATUS
} from '@/utils/static/kyc';
import { RejectModal } from '@/modules/Questionnaire/modals';
import { kycHelpers } from '@/utils/helpers/kyc';
import { useTranslation } from 'react-i18next';
import { ProductView } from '@/modules';
import { AddProductContractNumber } from '@/modules/Product/modals';
import { usePopups, useEntityStatus, useLoadingInit, useLoadingState } from '@/hooks';

export const ProductAnswers = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id, productId } = useParams();
  const { state } = useLocation();
  const { showErrorToast, showInfoToast } = usePopups();
  const [currentSection, setCurrentSection] = useState(0);
  const [rejectedQuestions, setRejectedQuestions] = useState(null);
  const [isModalRejectActive, setModalRejectActive] = useState(false);
  const [isModalAddContractNumberActive, setModalAddContractNumberActive] = useState(false);
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState('');

  const { data: res, isFetching } = useQuery({
    queryKey: ['getProductAnswers'],
    queryFn: () => adminKYCApi.getProductAnswers({ investorId: id, investorProductId: productId }),
    cacheTime: Infinity,
    onSuccess: async ({ data: product }) => {
      try {
        if (product?.status === STATUS.QUESTION_UNACCEPTED || product?.status === STATUS.REJECTED) {
          const res = await adminKYCApi.getProductRejectedEntities(id);
          const rejectedQuestions = res?.data?.question;

          const { rejectedObj } = kycHelpers.getProductRejectedObject(product, rejectedQuestions);

          if (rejectedObj) setRejectedQuestions(rejectedObj);
        }
      } catch (e) {
        showErrorToast(e, true);
      }
    }
  });
  const { mutateAsync: setProductStatus } = useMutation({
    mutationFn: ({ status, comment }) =>
      adminKYCApi.setProductStatus({
        investorId: id,
        status: status,
        productId: productId,
        comment: comment
      })
  });

  useLoadingInit();
  useLoadingState(isFetching);

  const stepsCount = useMemo(() => {
    if (res?.data?.productSections)
      return Math.ceil(res.data.productSections.length / SHOWED_SECTIONS_COUNT);
    return 0;
  }, [res]);

  const { isRejected, isApproved } = useEntityStatus(res?.data);

  const statusSettings = useMemo(() => {
    if (res?.data) {
      const status = res?.data?.status;
      return {
        isRejected,
        isApproved,
        allowReject: !isRejected && ENABLED_QUESTS_TO_INTERACT_STATUSES_ADMIN[status],
        allowApprove: !isApproved && ENABLED_QUESTS_TO_INTERACT_STATUSES_ADMIN[status]
      };
    }

    return null;
  }, [res, isRejected, isApproved]);

  const rejectProduct = async () => {
    try {
      if (comment) {
        const status = await setProductStatus({ status: STATUS.REJECTED, comment });
        if (status) {
          showInfoToast('Rejected');
          navigate(`/investor/${id}`);
        }
      } else {
        setCommentError(t('Required'));
      }
    } catch (e) {
      showErrorToast(e, true);
    }
  };
  const acceptProduct = async () => {
    try {
      const status = await setProductStatus({ status: STATUS.PASSED });
      if (status) {
        showInfoToast('Accepted');
        navigate(`/investor/${id}`);
      }
    } catch (e) {
      showErrorToast(e, true);
      console.error(e);
    }
  };

  const onNext = () => {
    if (currentSection + 1 < stepsCount) {
      setCurrentSection(currentSection + 1);
      scrollToTop();
    }
  };
  const onBack = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
      scrollToTop();
    } else {
      state && state.prevPath ? navigate(state.prevPath) : navigate(-1);
    }
  };

  const addRejectedQuestion = (questionId, comment) => {
    setRejectedQuestions({ ...rejectedQuestions, [questionId]: comment });
  };

  return (
    <div>
      {!isFetching && res?.data && (
        <ProductView
          product={res.data}
          currentSection={currentSection}
          onNext={onNext}
          onBack={onBack}
          adminView
          rejectProduct={() => setModalRejectActive(true)}
          acceptProduct={() => setModalAddContractNumberActive(true)}
          rejectedQuestions={rejectedQuestions}
          addRejectedQuestion={addRejectedQuestion}
          statusSettings={statusSettings}
          stepsCount={stepsCount}
          setCurrentSection={setCurrentSection}
        />
      )}
      {!isFetching && !res?.data && <div>{t('Something went wrong')}</div>}

      <RejectModal
        active={isModalRejectActive}
        onClose={() => setModalRejectActive(false)}
        comment={comment}
        setComment={setComment}
        error={commentError}
        onSubmit={rejectProduct}
      />

      <AddProductContractNumber
        active={isModalAddContractNumberActive}
        onClose={() => setModalAddContractNumberActive(false)}
        onSubmit={acceptProduct}
        investorId={id}
        productId={productId}
      />
    </div>
  );
};
